/**
 * MasterTemplatesConfig — «Свойства и категории» (Matching v2 §B-FE).
 *
 * Два sub-tab'а: Global (только superadmin) и User.
 * Каждый: список загруженных баз фильтров (маркетплейс / файл / дата / версия /
 * статус / % прогресса) + dropzone + Reparse/Delete.
 * Drawer — сводка разбора (counts); таксономия целиком в UI не показывается
 * (см. фикс утечки: раньше печатали весь JSON.stringify(mt.taxonomy)).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Library,
  Upload,
  Download,
  Trash2,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Clock,
  X,
} from 'lucide-react';

import {
  useDeleteMasterTemplate,
  useDownloadBlankTemplate,
  useMasterTemplate,
  useMasterTemplates,
  useReparseMasterTemplate,
  useUploadMasterTemplate,
  type MasterTemplate,
  type MasterTemplateKey,
  type MasterTemplateScope,
  type MasterTemplateStatus,
} from '@/hooks/useAdmin';
import { cn } from '@/lib/utils';

type SubTabKey = 'global' | 'user';

export function MasterTemplatesConfig() {
  const { t } = useTranslation();
  const [sub, setSub] = useState<SubTabKey>('user');
  const [viewId, setViewId] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">{t('system.masterTemplates.title')}</h2>
        <p className="text-sm text-surface-400">{t('system.masterTemplates.subtitle')}</p>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 border-b border-surface-800">
        <SubTabButton active={sub === 'user'} onClick={() => setSub('user')}>
          {t('system.masterTemplates.tabs.user')}
        </SubTabButton>
        <SubTabButton active={sub === 'global'} onClick={() => setSub('global')}>
          {t('system.masterTemplates.tabs.global')}
        </SubTabButton>
      </div>

      {sub === 'global' ? (
        <MasterTemplateList scope="global" onView={setViewId} />
      ) : (
        <MasterTemplateList scope="user" onView={setViewId} />
      )}

      {viewId && (
        <TaxonomyDrawer id={viewId} scope={sub} onClose={() => setViewId(null)} />
      )}
    </div>
  );
}

function SubTabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
        active
          ? 'border-primary-500 text-primary-300'
          : 'border-transparent text-surface-400 hover:text-surface-200',
      )}
    >
      {children}
    </button>
  );
}

function MasterTemplateList({
  scope,
  onView,
}: {
  scope: MasterTemplateScope;
  onView: (id: string) => void;
}) {
  const { t } = useTranslation();
  const { data: templates, isLoading } = useMasterTemplates(scope);
  const upload = useUploadMasterTemplate();
  const reparse = useReparseMasterTemplate();
  const del = useDeleteMasterTemplate();
  const download = useDownloadBlankTemplate();

  const [dragOver, setDragOver] = useState(false);

  const handleFile = (file: File) => {
    const ok = ['.xlsx', '.xlsm', '.json'].some((ext) =>
      file.name.toLowerCase().endsWith(ext),
    );
    if (!ok) {
      alert(t('system.masterTemplates.errors.wrong_type'));
      return;
    }
    upload.mutate({ scope, file });
  };

  return (
    <div className="space-y-4">
      {/* Dropzone + download blank */}
      <div className="flex items-center gap-3 flex-wrap">
        <label
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            const f = e.dataTransfer.files[0];
            if (f) handleFile(f);
          }}
          className={cn(
            'flex-1 min-w-[280px] flex items-center justify-center gap-2 px-4 py-6 border-2 border-dashed rounded-lg cursor-pointer transition-colors',
            dragOver
              ? 'border-primary-500 bg-primary-500/10'
              : 'border-surface-700 hover:border-surface-600',
          )}
        >
          <Upload className="w-5 h-5 text-surface-400" />
          <span className="text-sm text-surface-300">
            {upload.isPending
              ? t('system.masterTemplates.uploading')
              : t('system.masterTemplates.dropzone')}
          </span>
          <input
            type="file"
            accept=".xlsx,.xlsm,.json"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
              e.target.value = ''; // allow re-upload of same file
            }}
          />
        </label>
        <button
          onClick={() => download.mutate()}
          disabled={download.isPending}
          className="btn-ghost flex items-center gap-2"
        >
          <Download className="w-4 h-4" />
          {t('system.masterTemplates.blank')}
        </button>
      </div>

      {upload.isError && (
        <div className="card border-danger-tint text-sm text-danger">
          {t('common.error')}: {String((upload.error as Error)?.message ?? upload.error)}
        </div>
      )}

      {/* Список */}
      {isLoading ? (
        <div className="text-sm text-surface-400">{t('common.loading')}</div>
      ) : !templates || templates.length === 0 ? (
        <div className="card text-sm text-surface-400 text-center py-8">
          <Library className="w-8 h-8 mx-auto mb-2 text-surface-500" />
          {t('system.masterTemplates.empty')}
        </div>
      ) : (
        <div className="space-y-2">
          {templates.map((mt) => (
            <MasterTemplateRow
              key={mt.id}
              mt={mt}
              onView={() => onView(mt.id)}
              onReparse={() => reparse.mutate({ id: mt.id, scope })}
              onDelete={() => {
                if (confirm(t('system.masterTemplates.delete_confirm', { name: mt.display_name }))) {
                  del.mutate({ id: mt.id, scope });
                }
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function MasterTemplateRow({
  mt,
  onView,
  onReparse,
  onDelete,
}: {
  mt: MasterTemplate;
  onView: () => void;
  onReparse: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  // §B-FE: в списке — маркетплейс / файл / дата / версия / статус / % прогресса.
  const showProgress = mt.status === 'uploading' || mt.status === 'parsing' || mt.status === 'ready';
  const pct = mt.status === 'ready' ? 100 : Math.max(0, Math.min(100, mt.progress ?? 0));
  return (
    <div className="card flex items-center gap-3">
      <StatusBadge status={mt.status} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium truncate">{mt.display_name}</span>
          <span className="text-xs px-1.5 py-0.5 rounded bg-surface-800 text-surface-300">
            {t('system.masterTemplates.sources.' + mt.key)}
          </span>
          <span className="text-xs text-surface-500">
            {t('system.masterTemplates.row.version')}: {mt.version}
          </span>
        </div>
        <div className="text-xs text-surface-500 truncate">
          {mt.original_filename} · {formatBytes(mt.file_size_bytes)} ·{' '}
          {new Date(mt.created_at).toLocaleString()}
        </div>
        {showProgress && (
          <div className="mt-1 flex items-center gap-2">
            <div className="h-1.5 flex-1 max-w-[220px] rounded-full bg-surface-800 overflow-hidden">
              <div
                className={cn(
                  'h-full rounded-full transition-all',
                  mt.status === 'ready' ? 'bg-success' : 'bg-primary-500',
                )}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-xs text-surface-400 tabular-nums w-10 text-right">
              {pct}%
            </span>
          </div>
        )}
        {mt.status === 'ready' && Object.keys(mt.parsed_counts ?? {}).length > 0 && (
          <div className="text-xs text-surface-500 mt-0.5">
            {t('system.masterTemplates.row.counts', {
              counts: Object.entries(mt.parsed_counts)
                .map(([k, v]) => `${k}: ${v}`)
                .join(', '),
            })}
          </div>
        )}
        {mt.status === 'error' && mt.error_message && (
          <div className="text-xs text-danger mt-1 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {mt.error_message}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={onView}
          disabled={mt.status !== 'ready'}
          className="btn-ghost text-xs"
          title={t('system.masterTemplates.view')}
        >
          {t('system.masterTemplates.view')}
        </button>
        <button onClick={onReparse} className="btn-ghost p-2" title={t('system.masterTemplates.reparse')}>
          <RefreshCw className="w-4 h-4" />
        </button>
        <button onClick={onDelete} className="btn-ghost p-2 text-danger" title={t('common.delete')}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: MasterTemplateStatus }) {
  const { t } = useTranslation();
  const map: Record<MasterTemplateStatus, { icon: React.ReactNode; cls: string }> = {
    uploading: { icon: <Clock className="w-4 h-4" />, cls: 'text-warning' },
    parsing: { icon: <RefreshCw className="w-4 h-4 animate-spin" />, cls: 'text-info400' },
    ready: { icon: <CheckCircle2 className="w-4 h-4" />, cls: 'text-success' },
    error: { icon: <AlertCircle className="w-4 h-4" />, cls: 'text-danger' },
  };
  return (
    <span className={cn('flex items-center gap-1 text-xs whitespace-nowrap', map[status].cls)}>
      {map[status].icon}
      {t('system.masterTemplates.status.' + status)}
    </span>
  );
}

function TaxonomyDrawer({
  id,
  scope,
  onClose,
}: {
  id: string;
  scope: MasterTemplateScope;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { data: mt, isLoading } = useMasterTemplate(id, scope);
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex justify-end" onClick={onClose}>
      <div
        className="w-full max-w-2xl bg-surface-900 border-l border-surface-800 p-6 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">
            {mt?.display_name ?? t('common.loading')}
          </h3>
          <button onClick={onClose} className="btn-ghost p-2">
            <X className="w-4 h-4" />
          </button>
        </div>
        {isLoading || !mt ? (
          <div className="text-sm text-surface-400">{t('common.loading')}</div>
        ) : (
          // Фикс утечки: раньше здесь печатался весь JSON таксономии
          // (JSON.stringify(mt.taxonomy) — 9–34 МБ JSONB в DOM). Теперь только сводка.
          <div className="space-y-3 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-surface-400">{t('system.masterTemplates.row.marketplace')}</span>
              <span>{t('system.masterTemplates.sources.' + mt.key)}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-surface-400">{t('system.masterTemplates.row.version')}</span>
              <span>{mt.version}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-surface-400">{t('system.masterTemplates.row.progress')}</span>
              <span>{mt.status === 'ready' ? 100 : Math.max(0, Math.min(100, mt.progress ?? 0))}%</span>
            </div>
            {Object.keys(mt.parsed_counts ?? {}).length > 0 && (
              <div className="text-surface-400 border-t border-surface-800 pt-2">
                {t('system.masterTemplates.row.counts', {
                  counts: Object.entries(mt.parsed_counts)
                    .map(([k, v]) => `${k}: ${v}`)
                    .join(', '),
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
